interface TokenResponse {
  [key: string]: string | undefined
}

interface TokenManagerConfig {
  tokenEndpoint?: string
  tokenField?: string
}

class TokenManager {
  private cachedToken: string | null = null

  private tokenPromise: Promise<string | null> | null = null

  private readonly tokenEndpoint: string

  private readonly tokenField: string

  constructor(config: TokenManagerConfig = {}) {
    this.tokenEndpoint = config.tokenEndpoint || '/api/auth/token'
    this.tokenField = config.tokenField || 'access_token'
  }

  private async fetchToken(): Promise<string | null> {
    try {
      const response = await fetch(this.tokenEndpoint, {
        credentials: 'include',
      })

      if (response.ok) {
        const data: TokenResponse = await response.json()
        return data[this.tokenField] || null
      }
      this.cachedToken = null
      return null
    } catch (_error) {
      this.cachedToken = null
      return null
    }
  }

  async getToken(): Promise<string | null> {
    if (this.cachedToken) {
      return this.cachedToken
    }

    if (this.tokenPromise) {
      return this.tokenPromise
    }

    this.tokenPromise = this.fetchToken()
    this.cachedToken = await this.tokenPromise
    this.tokenPromise = null

    return this.cachedToken
  }

  clearCache(): void {
    this.cachedToken = null
    this.tokenPromise = null
  }

  hasToken(): boolean {
    return this.cachedToken !== null
  }

  // Method to manually set a token (useful for testing or initial login)
  setToken(token: string | null): void {
    this.cachedToken = token
    this.tokenPromise = null
  }
}

// Create and export a singleton instance
export const tokenManager = new TokenManager()

// Export the class for creating custom instances if needed
export { TokenManager }
